import React from 'react';
import { Btn } from './index.jsx';
import { api } from '../lib/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import { palette as COLORS, type } from '../design';

// Mirrors DECLINE_REASON_MAX_LENGTH in backend/src/lib/constants.js; the API refuses anything longer.
export const DECLINE_REASON_MAX_LENGTH = 300;
export const DECLINE_FAILED_MESSAGE = 'We could not decline this booking. Check your connection and try again.';
const BAD_REQUEST_STATUS = 400;

const FONT = "'Plus Jakarta Sans', sans-serif";

// The message to show when a decline fails. A 400 carries the server's own words (the reason is
// too long); anything else reads as a failed decline the instructor can retry.
export function declineErrorMessage(err) {
  if (err?.status === BAD_REQUEST_STATUS && err.data?.message) return err.data.message;
  return DECLINE_FAILED_MESSAGE;
}

// The body for PATCH /bookings/:id/decline: the trimmed reason, or no body when there is none.
export function declineRequestArgs(bookingId, reason) {
  const path = `/bookings/${bookingId}/decline`;
  const note = (reason || '').trim();
  return note ? [path, { reason: note }] : [path];
}

// The confirm step a decline opens (IW-4.1). Nothing changes until the instructor taps Decline
// booking; Keep booking closes the step and sends nothing (IW-4.3). The optional reason goes into
// the student's email and nowhere else (IW-4.2).
export function DeclineConfirm({ busy = false, error = null, onConfirm, onCancel }) {
  const [reason, setReason] = React.useState('');
  const id = React.useId();
  const headingId = `${id}-heading`;
  const reasonId = `${id}-reason`;

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 340, padding: 12, borderRadius: 12, border: '1px solid ' + COLORS.border, background: COLORS.ivory, boxSizing: 'border-box' }}
    >
      <p id={headingId} style={{ fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: COLORS.espresso, margin: 0 }}>Decline this booking?</p>
      <p style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.driftwood, margin: 0, lineHeight: 1.5 }}>
        The student gets an email that you declined. You can add a reason to it.
      </p>
      <label htmlFor={reasonId} style={{ fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: COLORS.espresso }}>Reason for the student (optional)</label>
      <textarea
        id={reasonId}
        value={reason}
        maxLength={DECLINE_REASON_MAX_LENGTH}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="For example: Payment not received."
        style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid ' + COLORS.border, fontFamily: FONT, fontSize: type.body, color: COLORS.espresso, resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: COLORS.white }}
      />
      <p style={{ fontFamily: FONT, fontSize: type.caption, color: COLORS.driftwood, margin: 0, textAlign: 'right' }}>{reason.length} / {DECLINE_REASON_MAX_LENGTH}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={() => onConfirm?.(reason.trim())} disabled={busy}>Decline booking</Btn>
        <Btn size="sm" variant="subtle" onClick={() => onCancel?.()} disabled={busy}>Keep booking</Btn>
      </div>
      {error && (
        <span role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.errorRed, lineHeight: 1.5 }}>{error}</span>
      )}
    </div>
  );
}

// Decline a pending booking: tap Decline, then confirm with an optional reason. `onDeclined`
// refreshes the caller's data; a failure there reads as a failed decline.
export function DeclineBookingButton({ booking, onDeclined }) {
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const handleConfirm = async (reason) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(...declineRequestArgs(booking.id, reason));
      if (onDeclined) await onDeclined(booking);
      showSnackbar('Booking declined.', { variant: 'success' });
      close();
    } catch (err) {
      setError(declineErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return <Btn size="sm" variant="ghost" onClick={() => setOpen(true)}>Decline</Btn>;
  return <DeclineConfirm busy={busy} error={error} onConfirm={handleConfirm} onCancel={close} />;
}
